import React, { useRef, useCallback, useEffect } from "react"
import { IconButton } from "@mui/material"
import { Plus, Minus } from "lucide-mui"
import { PLAN_ZOOM_INTERVAL_MS } from "@/constants/ui"

const STYLES = {
  controls: "flex items-center gap-1",
  button: "bg-white border border-solid border-[#e5e7eb] shadow-sm hover:bg-gray-50",
  icon: "w-3.5 h-3.5 text-[#5f6774]",
} as const

interface PlanZoomControlsProps {
  zoom: number
  onZoomIn: () => void
  onZoomOut: () => void
}

const PlanZoomControls: React.FC<PlanZoomControlsProps> = ({ zoom, onZoomIn, onZoomOut }) => {
  const zoomIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const stopZoom = useCallback(() => {
    if (zoomIntervalRef.current) {
      window.clearTimeout(zoomIntervalRef.current)
      zoomIntervalRef.current = null
    }
  }, [])

  const startZoom = useCallback(
    (direction: "in" | "out") => {
      stopZoom()
      const performZoom = direction === "in" ? onZoomIn : onZoomOut
      performZoom()
      zoomIntervalRef.current = window.setInterval(performZoom, PLAN_ZOOM_INTERVAL_MS)
    },
    [onZoomIn, onZoomOut, stopZoom],
  )

  useEffect(() => stopZoom, [stopZoom])

  return (
    <div className={STYLES.controls}>
      <IconButton
        onMouseDown={() => startZoom("out")}
        onMouseUp={stopZoom}
        onMouseLeave={stopZoom}
        onTouchStart={() => startZoom("out")}
        onTouchEnd={stopZoom}
        disabled={zoom <= 100}
        size="small"
        className={STYLES.button}
        sx={{ borderRadius: "2.5px", width: 23, height: 23, minWidth: 0, padding: 0 }}
        data-testid="plan-viewer-zoom-out"
      >
        <Minus className={STYLES.icon} />
      </IconButton>
      <IconButton
        onMouseDown={() => startZoom("in")}
        onMouseUp={stopZoom}
        onMouseLeave={stopZoom}
        onTouchStart={() => startZoom("in")}
        onTouchEnd={stopZoom}
        disabled={zoom >= 400}
        size="small"
        className={STYLES.button}
        sx={{ borderRadius: "2.5px", width: 23, height: 23, minWidth: 0, padding: 0 }}
        data-testid="plan-viewer-zoom-in"
      >
        <Plus className={STYLES.icon} />
      </IconButton>
    </div>
  )
}

export default PlanZoomControls
